/**
 * Traced Email Actions Service
 * 
 * Wrapper around emailActions with full API tracing enabled.
 * All Microsoft Graph/Gmail API calls are traced for monitoring and debugging.
 */

import { emailActions, EmailDraft, EmailAction, SearchOptions, FilterRule } from './emailActions';
import { apiTracer, APITraceEntry } from './apiTracer';

/**
 * Enhanced Email Actions with API Tracing
 */
export class TracedEmailActionsService {
  private userId: string | null = null;

  setUserId(userId: string) {
    this.userId = userId;
    emailActions.setUserId(userId);
  }

  /**
   * Traced: Send Email
   */
  async sendEmail(emailData: {
    to: string[];
    cc?: string[];
    bcc?: string[];
    subject: string;
    body: string;
    htmlBody?: string;
    attachments?: any[];
    scheduledSend?: Date;
    importance?: 'high' | 'normal' | 'low';
    requestReadReceipt?: boolean;
    requestDeliveryReceipt?: boolean;
  }): Promise<{ success: boolean; messageId: string }> {
    return await apiTracer.traceAPICall(
      'email-agent',
      'send_email',
      'microsoft', // Will be detected automatically
      {
        method: 'POST',
        url: 'https://graph.microsoft.com/v1.0/me/sendMail',
        body: emailData
      },
      {
        userId: this.userId || undefined,
        agentAction: 'email_send',
        userIntent: `Send email to ${emailData.to.join(', ')}`
      },
      async () => {
        return await emailActions.sendEmail(emailData);
      }
    );
  }

  /**
   * Traced: Search Emails
   */
  async search(options: SearchOptions): Promise<any[]> {
    return await apiTracer.traceAPICall(
      'email-agent',
      'search_emails',
      'microsoft',
      {
        method: 'POST',
        url: 'https://ben3ou87--outlook-search.functions.blink.new',
        body: options
      },
      {
        userId: this.userId || undefined,
        agentAction: 'email_search',
        userIntent: `Search emails: "${options.query}"`
      },
      async () => {
        return await emailActions.search(options);
      }
    );
  }

  /**
   * Traced: Create Draft
   */
  async createDraft(draft: Partial<EmailDraft>): Promise<EmailDraft> {
    return await apiTracer.traceAPICall(
      'email-agent',
      'create_draft',
      'internal',
      {
        method: 'POST',
        url: '/api/drafts',
        body: draft
      },
      {
        userId: this.userId || undefined,
        agentAction: 'email_draft',
        userIntent: `Create draft: "${draft.subject}"`
      },
      async () => {
        return await emailActions.createDraft(draft);
      }
    );
  }

  /**
   * Traced: Reply
   */
  async reply(emailId: string, replyText: string, replyAll: boolean = false): Promise<void> {
    return await apiTracer.traceAPICall(
      'email-agent',
      'reply_email',
      'microsoft',
      {
        method: 'POST',
        url: `https://graph.microsoft.com/v1.0/me/messages/${emailId}/reply`,
        body: { replyText, replyAll }
      },
      {
        userId: this.userId || undefined,
        agentAction: 'email_reply',
        userIntent: replyAll ? 'Reply all to email' : 'Reply to email'
      },
      async () => {
        return await emailActions.reply(emailId, replyText, replyAll);
      }
    );
  }

  /**
   * Traced: Forward
   */
  async forward(emailId: string, to: string[], message?: string): Promise<void> {
    return await apiTracer.traceAPICall(
      'email-agent',
      'forward_email',
      'microsoft',
      {
        method: 'POST',
        url: `https://graph.microsoft.com/v1.0/me/messages/${emailId}/forward`,
        body: { to, message }
      },
      {
        userId: this.userId || undefined,
        agentAction: 'email_forward',
        userIntent: `Forward email to ${to.join(', ')}`
      },
      async () => {
        return await emailActions.forward(emailId, to, message);
      }
    );
  }

  /**
   * Traced: Move to Folder
   */
  async moveToFolder(emailId: string, folder: string): Promise<void> {
    return await apiTracer.traceAPICall(
      'email-agent',
      'move_email',
      'microsoft',
      {
        method: 'POST',
        url: 'https://ben3ou87--outlook-move.functions.blink.new',
        body: { messageId: emailId, destinationFolder: folder, operation: 'move' }
      },
      {
        userId: this.userId || undefined,
        agentAction: 'email_move',
        userIntent: `Move email to ${folder}`
      },
      async () => {
        return await emailActions.moveToFolder(emailId, folder);
      }
    );
  }

  /**
   * Traced: Delete Email
   */
  async deleteEmail(emailId: string): Promise<void> {
    return await apiTracer.traceAPICall(
      'email-agent',
      'delete_email',
      'microsoft',
      {
        method: 'DELETE',
        url: `https://graph.microsoft.com/v1.0/me/messages/${emailId}`,
        body: {}
      },
      {
        userId: this.userId || undefined,
        agentAction: 'email_delete',
        userIntent: 'Delete email'
      },
      async () => {
        return await emailActions.deleteEmail(emailId);
      }
    );
  }

  /**
   * Traced: Archive
   */
  async archive(emailId: string): Promise<void> {
    return await apiTracer.traceAPICall(
      'email-agent',
      'archive_email',
      'internal',
      {
        method: 'POST',
        url: `/api/emails/${emailId}/archive`,
        body: {}
      },
      {
        userId: this.userId || undefined,
        agentAction: 'email_archive',
        userIntent: 'Archive email'
      },
      async () => {
        return await emailActions.archive(emailId);
      }
    );
  }

  /**
   * Traced: Mark Spam
   */
  async markSpam(emailId: string): Promise<void> {
    return await apiTracer.traceAPICall(
      'email-agent',
      'mark_spam',
      'internal',
      {
        method: 'POST',
        url: `/api/emails/${emailId}/spam`,
        body: {}
      },
      {
        userId: this.userId || undefined,
        agentAction: 'email_spam',
        userIntent: 'Mark email as spam'
      },
      async () => {
        return await emailActions.markSpam(emailId);
      }
    );
  }

  /**
   * Traced: Search Emails (alias method for workflow compatibility)
   */
  async searchEmails(query: string, options?: Partial<SearchOptions>): Promise<{ emails: any[]; total: number }> {
    const searchOptions: SearchOptions = {
      query,
      ...options
    };
    
    const results = await this.search(searchOptions);
    
    return {
      emails: results,
      total: results.length
    };
  }

  // Pass through other methods without tracing (internal operations)
  updateDraft = emailActions.updateDraft.bind(emailActions);
  deleteDraft = emailActions.deleteDraft.bind(emailActions);
  markRead = emailActions.markRead.bind(emailActions);
  star = emailActions.star.bind(emailActions);
  markImportant = emailActions.markImportant.bind(emailActions);
  trash = emailActions.trash.bind(emailActions);
  addLabel = emailActions.addLabel.bind(emailActions);
  removeLabel = emailActions.removeLabel.bind(emailActions);
  snooze = emailActions.snooze.bind(emailActions);
  mute = emailActions.mute.bind(emailActions);
  pin = emailActions.pin.bind(emailActions);
  reportPhishing = emailActions.reportPhishing.bind(emailActions);
  blockSender = emailActions.blockSender.bind(emailActions);
  unsubscribe = emailActions.unsubscribe.bind(emailActions);
  createRule = emailActions.createRule.bind(emailActions);
  applyRule = emailActions.applyRule.bind(emailActions);
  setAutoReply = emailActions.setAutoReply.bind(emailActions);
}

export const tracedEmailActions = new TracedEmailActionsService();
